/**
 * Created by neo on 28.08.2024
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
  MediaController,
  MediaControlBar,
  MediaTimeRange,
  MediaTimeDisplay,
  MediaVolumeRange,
  MediaPlayButton,
  MediaMuteButton,
  MediaFullscreenButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaPlaybackRateButton,
  MediaPipButton,
} from 'media-chrome/react';
import Hls from 'hls.js';
import { Media, getMediaMediumOrSmallest } from '../../Model/Media/Media';
import { notUndefined } from '../../Utils/notUndefined';
import { useLanguage } from '../../Hooks/useLanguage';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Theme } from '../../Theme/Theme';
import { MediaAnalyticsConfig, useMediaAnalytics } from '../../Hooks/useMediaAnalytics';
import { usePlausible } from '../../Contexts/PlausibleContext';

const PlayerWrapper = styled.div`
  width: 100%;
`;

const PlayerContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
  
  @media (max-width: 768px) {
    border-radius: 0;
  }
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #000;
`;

const AiTranslatedContainer = styled.div`
  background: linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.primaryDark});
  color: white;
  padding: 8px 14px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &::before {
    content: '✨';
    font-size: 14px;
  }
`;

const CenteredChromeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const AiMessageContainer = styled.div`
  background: rgba(0, 0, 0, 0.95);
  color: white;
  padding: 28px 32px;
  border-radius: 20px;
  text-align: center;
  max-width: 80%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);

  p {
    margin: 0 0 16px 0;
    font-size: 15px;
    line-height: 1.6;
  }
`;

const CloseAiMessageButton = styled.button`
  padding: 12px 24px;
  background: ${Theme.Colors.primary};
  color: white;
  border-radius: 24px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: background 0.2s ease;

  &:hover {
    background: ${Theme.Colors.primaryDark};
  }
`;

const AiCountdownTooltip = styled.div`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 30;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  max-width: 90%;
`;

const CountdownCircle = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
`;

const CountdownText = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  opacity: 0.9;
`;

const StyledMediaControlBar = styled(MediaControlBar)`
  --media-control-background: transparent;
  --media-control-hover-background: transparent;
  --media-primary-color: rgba(255, 255, 255, 0.95);
  --media-secondary-color: ${Theme.Colors.primary};
  --media-range-track-background: rgba(255, 255, 255, 0.15);
  --media-range-track-border-radius: 100px;
  --media-range-track-height: 4px;
  --media-range-thumb-background: white;
  --media-range-thumb-width: 12px;
  --media-range-thumb-height: 12px;
  --media-range-thumb-border-radius: 50%;
  --media-range-bar-color: ${Theme.Colors.primary};
  --media-time-range-buffered-color: rgba(255, 255, 255, 0.3);
  --media-control-padding: 6px;
  --media-font-family: inherit;

  background: linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.4) 60%, transparent 100%);
  padding: 24px 16px 14px;
  border-radius: 0 0 16px 16px;
  backdrop-filter: blur(4px);
  gap: 4px;

  media-controller[mediaisfullscreen] & {
    border-radius: 0;
  }

  /* Actionable buttons - have hover effects */
  media-play-button,
  media-seek-backward-button,
  media-seek-forward-button,
  media-mute-button,
  media-pip-button,
  media-fullscreen-button,
  media-playback-rate-button {
    --media-button-icon-width: 20px;
    --media-button-icon-height: 20px;
    border-radius: 6px;
    padding: 6px;
    transition: background 0.15s ease, transform 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  media-play-button {
    --media-button-icon-width: 24px;
    --media-button-icon-height: 24px;
  }

  /* Time display - no hover, static info */
  media-time-display {
    font-size: 12px;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
    opacity: 0.85;
    padding: 0 4px;
    pointer-events: none;
  }

  /* Seek bar - functional but no flashy hover */
  media-time-range {
    --media-range-track-height: 4px;
    --media-range-thumb-opacity: 0;
    flex-grow: 1;
    margin: 0 8px;

    &:hover {
      --media-range-track-height: 6px;
      --media-range-thumb-opacity: 1;
    }
  }

  /* Volume - smaller, subtle */
  media-volume-range {
    --media-range-track-height: 3px;
    --media-range-thumb-width: 10px;
    --media-range-thumb-height: 10px;
    width: 70px;
    opacity: 0.9;

    @media (max-width: 600px) {
      width: 50px;
    }
  }

  /* Playback rate button */
  media-playback-rate-button {
    font-size: 12px;
    font-weight: 600;
    min-width: 40px;
  }
`;

const TopChromeContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  padding: 12px;
  box-sizing: border-box;
`;

const LanguageSelectorContainer = styled.div`
  display: flex;
  gap: 6px;
  margin-left: auto;
`;

const FlagButton = styled.button<{ $isSelected: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${({ $isSelected }) => ($isSelected ? Theme.Colors.primary : 'rgba(255, 255, 255, 0.3)')};
  background: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  overflow: hidden;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s ease;
  box-shadow: ${({ $isSelected }) => ($isSelected ? `0 0 0 2px ${Theme.Colors.primary}40` : 'none')};

  &:hover {
    border-color: ${Theme.Colors.primary};
    transform: scale(1.1);
  }
`;

const languageToFlag: Record<string, string> = {
  en: '🇬🇧',
  de: '🇩🇪',
  es: '🇪🇸',
  fr: '🇫🇷',
  it: '🇮🇹',
  pt: '🇵🇹',
  nl: '🇳🇱',
  sv: '🇸🇪',
  da: '🇩🇰',
  fi: '🇫🇮',
  no: '🇳🇴',
  pl: '🇵🇱',
  cs: '🇨🇿',
  ro: '🇷🇴',
  hu: '🇭🇺',
  el: '🇬🇷',
  bg: '🇧🇬',
  hr: '🇭🇷',
  lt: '🇱🇹',
  lv: '🇱🇻',
  et: '🇪🇪',
  sk: '🇸🇰',
  sl: '🇸🇮',
};

const languageToName: Record<string, string> = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  pt: 'Português',
  nl: 'Nederlands',
  sv: 'Svenska',
  da: 'Dansk',
  fi: 'Suomi',
  no: 'Norsk',
  pl: 'Polski',
  cs: 'Čeština',
  ro: 'Română',
  hu: 'Magyar',
  el: 'Ελληνικά',
  bg: 'Български',
  hr: 'Hrvatski',
  lt: 'Lietuvių',
  lv: 'Latviešu',
  et: 'Eesti',
  sk: 'Slovenčina',
  sl: 'Slovenščina',
};

const QualityMenuContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const QualityButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.95);
  cursor: pointer;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background 0.15s ease, transform 0.15s ease;
  min-width: 60px;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const QualityMenu = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 8px;
  padding: 4px 0;
  min-width: 120px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  z-index: 100;
`;

const QualityMenuItem = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  background: ${({ $isSelected }) => ($isSelected ? 'rgba(255, 255, 255, 0.15)' : 'transparent')};
  border: none;
  color: white;
  padding: 8px 16px;
  text-align: left;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  span:last-child {
    color: ${({ $isSelected }) => ($isSelected ? Theme.Colors.primary : 'rgba(255, 255, 255, 0.5)')};
    font-size: 11px;
  }
`;

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
  </svg>
);

export type KinasticVideoPlayerProps = {
  videos: Media[];
  title?: string;
  /** Content entry id, attached to Plausible Page View / Watch Progress / Pause / Complete events when provided. */
  entryId?: string;
  showLanguageSelector?: boolean;
  onWatchDuration?: (watchedDurationInSeconds: number, totalDurationInSeconds: number) => void;
  /** optional callback for time updates (in seconds) */
  onProgress?: (playedSeconds: number) => void;
  hideFullscreenButton?: boolean;
  analyticsConfig?: MediaAnalyticsConfig;
};

export const KinasticVideoPlayer: React.FC<KinasticVideoPlayerProps> = observer(
  ({ videos, title, entryId, showLanguageSelector = false, onWatchDuration, onProgress, hideFullscreenButton = false, analyticsConfig }) => {
    const { language: currentLanguage } = useLanguage();
    const { t } = useTrans();
    const mediaAnalytics = useMediaAnalytics(analyticsConfig);
    const { trackPlausible } = usePlausible();
    const prevPlaybackRateRef = useRef<number>(1);
    const sentMilestonesRef = useRef<Set<number>>(new Set());
    const contentCompleteRef = useRef(false);

    const [selectedLanguage, setSelectedLanguage] = useState(currentLanguage);
    const [showAIMessage, setShowAIMessage] = useState(false);
    const [countdown, setCountdown] = useState(6);
    const initialCountdownRef = useRef<number>(countdown);
    const [hasCountdownCompleted, setHasCountdownCompleted] = useState(false);
    const [totalDuration, setTotalDuration] = useState(0);
    const [hasVideoEnded, setHasVideoEnded] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [qualityLevels, setQualityLevels] = useState<Array<{ height: number; bitrate: number; index: number }>>([]);
    const [currentQuality, setCurrentQuality] = useState<number>(-1); // -1 = auto
    const [autoQualityLabel, setAutoQualityLabel] = useState<string>('');
    const [showQualityMenu, setShowQualityMenu] = useState(false);

    const currentTimeRef = useRef(0);
    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null);
    const playStartTimeRef = useRef<number | null>(null);
    const totalWatchedTimeRef = useRef<number>(0);
    const qualityMenuRef = useRef<HTMLDivElement>(null);

    const videoMedia =
      videos.find((v) => v.language === selectedLanguage) ?? videos.find((v) => v.language === 'en') ?? videos[0];

    const availableLanguages = useMemo(
      () =>
        videos
          .flatMap((s) => s.language)
          .filter((v) => !!v)
          .filter(notUndefined),
      [videos],
    );

    const streamUrl = useMemo(() => {
      if (!videoMedia?.apiVideoId) {
        return undefined;
      }
      return `https://vod.api.video/vod/${videoMedia.apiVideoId}/hls/manifest.m3u8`;
    }, [videoMedia?.apiVideoId]);

    const thumbnailUrl = videoMedia ? getMediaMediumOrSmallest(videoMedia) : undefined;

    React.useEffect(() => {
      trackPlausible('Page View', {
        contentType: 'video',
        ...(title ? { pageTitle: title } : {}),
        ...(entryId ? { contentId: entryId } : {}),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, entryId]);
    
    // Check for mobile
    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.matchMedia('(max-width: 768px)').matches);
      };
      checkMobile();
      const mediaQuery = window.matchMedia('(max-width: 768px)');
      mediaQuery.addEventListener('change', checkMobile);
      return () => mediaQuery.removeEventListener('change', checkMobile);
    }, []);

    // Close quality menu on outside click
    useEffect(() => {
      if (!showQualityMenu) return;
      const handleClickOutside = (event: MouseEvent) => {
        if (qualityMenuRef.current && !qualityMenuRef.current.contains(event.target as Node)) {
          setShowQualityMenu(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showQualityMenu]);

    // Setup HLS streaming
    useEffect(() => {
      const video = videoRef.current;
      if (!video || !streamUrl || !hasCountdownCompleted) return;

      const savedTime = currentTimeRef.current;

      if (Hls.isSupported()) {
        const hls = new Hls();
        hlsRef.current = hls;
        hls.loadSource(streamUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
          // Extract available quality levels
          const levels = data.levels.map((level, index) => ({
            height: level.height,
            bitrate: level.bitrate,
            index,
          })).sort((a, b) => b.height - a.height);
          setQualityLevels(levels);
          setCurrentQuality(-1); // Start with auto
          
          // Restore playback position if switching languages
          if (savedTime > 0) {
            video.currentTime = savedTime;
          }
          video.play().catch(console.error);
        });
        
        // Track when HLS switches levels (for auto quality display)
        hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
          const level = hls.levels[data.level];
          if (level) {
            setAutoQualityLabel(`${level.height}p`);
          }
        });

        return () => {
          hls.destroy();
          hlsRef.current = null;
          setQualityLevels([]);
          setCurrentQuality(-1);
          setAutoQualityLabel('');
        };
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = streamUrl;
        video.addEventListener('loadedmetadata', () => {
          // Restore playback position if switching languages
          if (savedTime > 0) {
            video.currentTime = savedTime;
          }
          video.play().catch(console.error);
        });
      }
    }, [streamUrl, hasCountdownCompleted]);

    // Reset state when video changes
    useEffect(() => {
      setHasVideoEnded(false);
      totalWatchedTimeRef.current = 0;
      playStartTimeRef.current = null;
      sentMilestonesRef.current = new Set();
      contentCompleteRef.current = false;
    }, [videoMedia]);

    // Countdown for AI content
    useEffect(() => {
      if (videoMedia?.aiContent) {
        if (countdown > 0 && !hasCountdownCompleted) {
          const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
          return () => clearTimeout(timer);
        } else if (!hasCountdownCompleted) {
          setHasCountdownCompleted(true);
        }
      } else {
        setHasCountdownCompleted(true);
      }
    }, [countdown, hasCountdownCompleted, videoMedia?.aiContent]);

    // Cleanup: report watch duration on unmount
    useEffect(() => {
      return () => {
        if (playStartTimeRef.current) {
          const duration = Date.now() - playStartTimeRef.current;
          totalWatchedTimeRef.current += duration;
        }

        if (!hasVideoEnded) {
          const watchedSeconds = Math.floor(totalWatchedTimeRef.current / 1000);
          if (watchedSeconds > 0) {
            onWatchDuration?.(watchedSeconds, totalDuration);
          }
        }
      };
    }, [onWatchDuration, totalDuration, hasVideoEnded]);

    const handleChangeLanguage = useCallback((lang: string) => {
      if (videoRef.current) {
        currentTimeRef.current = videoRef.current.currentTime;
      }
      mediaAnalytics.onLanguageChange(selectedLanguage, lang);
      setSelectedLanguage(lang);
    }, [selectedLanguage, mediaAnalytics]);

    const handleAIToggle = useCallback(() => {
      const video = videoRef.current;
      if (video) {
        if (video.paused) {
          video.play();
        } else {
          video.pause();
        }
      }
      setShowAIMessage((prev) => !prev);
    }, []);

    const handlePlay = useCallback(() => {
      playStartTimeRef.current = Date.now();
      setHasVideoEnded(false);
      mediaAnalytics.onPlay();
      trackPlausible('Start Watching', {
        contentType: 'video',
        ...(title ? { pageTitle: title } : {}),
        ...(entryId ? { contentId: entryId } : {}),
      });
    }, [mediaAnalytics, trackPlausible, title, entryId]);

    const handlePause = useCallback(() => {
      if (playStartTimeRef.current) {
        const duration = Date.now() - playStartTimeRef.current;
        totalWatchedTimeRef.current += duration;
        playStartTimeRef.current = null;
      }
      mediaAnalytics.onPause();
      const video = videoRef.current;
      if (video && !video.ended) {
        trackPlausible('Pause Watching', {
          seconds: `${Math.floor(video.currentTime)}s`,
          duration: `${Math.floor(totalDuration)}s`,
          contentType: 'video',
          ...(title ? { pageTitle: title } : {}),
          ...(entryId ? { contentId: entryId } : {}),
        });
      }
    }, [mediaAnalytics, trackPlausible, totalDuration, title, entryId]);

    const handleEnded = useCallback(() => {
      if (playStartTimeRef.current) {
        const duration = Date.now() - playStartTimeRef.current;
        totalWatchedTimeRef.current += duration;
        playStartTimeRef.current = null;
      }
      const watchedSeconds = Math.floor(totalWatchedTimeRef.current / 1000);
      onWatchDuration?.(watchedSeconds, totalDuration);
      setHasVideoEnded(true);
      mediaAnalytics.onEnded();
      if (!contentCompleteRef.current) {
        contentCompleteRef.current = true;
        trackPlausible('Content Complete', {
          seconds: `${watchedSeconds}s`,
          contentType: 'video',
          ...(title ? { pageTitle: title } : {}),
          ...(entryId ? { contentId: entryId } : {}),
        });
      }
    }, [onWatchDuration, totalDuration, mediaAnalytics, trackPlausible, title, entryId]);

    const handleTimeUpdate = useCallback(() => {
      const video = videoRef.current;
      if (video && video.currentTime > 0) {
        currentTimeRef.current = video.currentTime;
        onProgress?.(video.currentTime);
        mediaAnalytics.onTimeUpdate(video.currentTime, video.duration || 0);
        const duration = video.duration;
        if (duration > 0) {
          const pct = (video.currentTime / duration) * 100;
          const completeMilestone = 98;
          for (const milestone of [5, 10, 20, 40, 80, completeMilestone]) {
            if (pct >= milestone && !sentMilestonesRef.current.has(milestone)) {
              sentMilestonesRef.current.add(milestone);
              trackPlausible('Watch Progress', {
                milestone: `${milestone}%`,
                seconds: `${Math.floor(video.currentTime)}s`,
                duration: `${Math.floor(duration)}s`,
                contentType: 'video',
                ...(title ? { pageTitle: title } : {}),
                ...(entryId ? { contentId: entryId } : {}),
              });
              if (milestone === completeMilestone && !contentCompleteRef.current) {
                contentCompleteRef.current = true;
                trackPlausible('Content Complete', {
                  seconds: `${Math.floor(video.currentTime)}s`,
                  contentType: 'video',
                  ...(title ? { pageTitle: title } : {}),
                  ...(entryId ? { contentId: entryId } : {}),
                });
              }
            }
          }
        }
      }
    }, [onProgress, mediaAnalytics, trackPlausible, title, entryId]);

    const handleDurationChange = useCallback(() => {
      const video = videoRef.current;
      if (video) {
        setTotalDuration(video.duration);
      }
    }, []);

    const handleQualityChange = useCallback((levelIndex: number) => {
      const hls = hlsRef.current;
      if (hls) {
        const prevLabel = currentQuality === -1 ? 'auto' : `${qualityLevels.find((l) => l.index === currentQuality)?.height ?? 'auto'}p`;
        const newLabel = levelIndex === -1 ? 'auto' : `${qualityLevels.find((l) => l.index === levelIndex)?.height ?? 'unknown'}p`;
        mediaAnalytics.onQualityChange(prevLabel, newLabel);
        hls.currentLevel = levelIndex; // -1 for auto
        setCurrentQuality(levelIndex);
      }
      setShowQualityMenu(false);
    }, [currentQuality, qualityLevels, mediaAnalytics]);

    const toggleQualityMenu = useCallback(() => {
      setShowQualityMenu((prev) => !prev);
    }, []);

    const getQualityLabel = useCallback(() => {
      if (currentQuality === -1) {
        return autoQualityLabel ? `Auto (${autoQualityLabel})` : 'Auto';
      }
      const level = qualityLevels.find((l) => l.index === currentQuality);
      return level ? `${level.height}p` : 'Auto';
    }, [currentQuality, autoQualityLabel, qualityLevels]);

    if (!videoMedia) {
      return null;
    }

    return (
      <PlayerWrapper>
        <PlayerContainer>
        {streamUrl && hasCountdownCompleted ? (
          <MediaController
            style={{
              width: '100%',
              height: '100%',
              aspectRatio: '16/9',
              backgroundColor: 'transparent',
            }}
          >
            <StyledVideo
              ref={videoRef}
              slot="media"
              poster={thumbnailUrl}
              playsInline
              onTimeUpdate={handleTimeUpdate}
              onDurationChange={handleDurationChange}
              onPlay={handlePlay}
              onPause={handlePause}
              onEnded={handleEnded}
              onSeeking={mediaAnalytics.onSeeking}
              onSeeked={mediaAnalytics.onSeeked}
              onWaiting={mediaAnalytics.onWaiting}
              onPlaying={mediaAnalytics.onPlaying}
              onRateChange={(e) => {
                const newRate = (e.target as HTMLVideoElement).playbackRate;
                mediaAnalytics.onRateChange(prevPlaybackRateRef.current, newRate);
                prevPlaybackRateRef.current = newRate;
              }}
            />
            {(videoMedia?.aiContent || (showLanguageSelector && availableLanguages.length > 1)) && (
              <TopChromeContainer slot="top-chrome">
                {videoMedia?.aiContent ? (
                  <AiTranslatedContainer onClick={handleAIToggle}>
                    {t('video.aiContentToggle', { defaultValue: 'AI Translated' })}
                  </AiTranslatedContainer>
                ) : (
                  <div />
                )}
                {showLanguageSelector && availableLanguages.length > 1 && (
                  <LanguageSelectorContainer>
                    {availableLanguages.map((lang) => (
                      <FlagButton
                        key={lang}
                        $isSelected={lang === selectedLanguage}
                        onClick={() => handleChangeLanguage(lang)}
                        title={languageToName[lang] || lang}
                      >
                        {languageToFlag[lang] || lang.toUpperCase().slice(0, 2)}
                      </FlagButton>
                    ))}
                  </LanguageSelectorContainer>
                )}
              </TopChromeContainer>
            )}
            <StyledMediaControlBar>
              <MediaPlayButton />
              {!isMobile && (
                <>
                  <MediaSeekBackwardButton seekOffset={10} />
                  <MediaSeekForwardButton seekOffset={20} />
                </>
              )}
              <MediaTimeRange />
              <MediaTimeDisplay showDuration />
              {!isMobile && <MediaPlaybackRateButton />}
              {qualityLevels.length > 1 && (
                <QualityMenuContainer ref={qualityMenuRef}>
                  <QualityButton onClick={toggleQualityMenu} title="Quality">
                    <SettingsIcon />
                    {getQualityLabel()}
                  </QualityButton>
                  {showQualityMenu && (
                    <QualityMenu>
                      <QualityMenuItem
                        $isSelected={currentQuality === -1}
                        onClick={() => handleQualityChange(-1)}
                      >
                        <span>Auto</span>
                        {currentQuality === -1 && autoQualityLabel && (
                          <span>{autoQualityLabel}</span>
                        )}
                      </QualityMenuItem>
                      {qualityLevels.map((level) => (
                        <QualityMenuItem
                          key={level.index}
                          $isSelected={currentQuality === level.index}
                          onClick={() => handleQualityChange(level.index)}
                        >
                          <span>{level.height}p</span>
                        </QualityMenuItem>
                      ))}
                    </QualityMenu>
                  )}
                </QualityMenuContainer>
              )}
              <MediaMuteButton />
              <MediaVolumeRange />
              {!isMobile && <MediaPipButton />}
              {!hideFullscreenButton && <MediaFullscreenButton />}
            </StyledMediaControlBar>
            {showAIMessage && (
              <CenteredChromeContainer slot="centered-chrome">
                <AiMessageContainer>
                  <p>
                    {t('video.aiContentMessage', {
                      defaultValue:
                        'This video has been AI translated. Some artifacts may appear unnatural. Please be aware that the translation may not be perfect.',
                    })}
                  </p>
                  <CloseAiMessageButton onClick={handleAIToggle}>
                    {t('video.closeAiMessage', { defaultValue: 'Close' })}
                  </CloseAiMessageButton>
                </AiMessageContainer>
              </CenteredChromeContainer>
            )}
          </MediaController>
        ) : null}

        {videoMedia?.aiContent && !hasCountdownCompleted && (
          <AiCountdownTooltip>
            <CountdownCircle>
              <svg width={40} height={40} viewBox="0 0 40 40">
                <circle cx={20} cy={20} r={17} fill="transparent" stroke="rgba(255,255,255,0.2)" strokeWidth={3} />
                <circle
                  cx={20}
                  cy={20}
                  r={17}
                  fill="transparent"
                  stroke={Theme.Colors.primary}
                  strokeWidth={3}
                  strokeDasharray={`${2 * Math.PI * 17}`}
                  strokeDashoffset={-((1 - countdown / initialCountdownRef.current) * 2 * Math.PI * 17)}
                  transform="rotate(-90 20 20)"
                  style={{ transition: 'stroke-dashoffset 1s linear' }}
                  strokeLinecap="round"
                />
                <text x={20} y={20} textAnchor="middle" dy="0.35em" fill="white" fontSize={14} fontWeight="bold">
                  {countdown}
                </text>
              </svg>
            </CountdownCircle>
            <CountdownText>
              {t('video.aiCountdownMessage', {
                defaultValue: 'AI translated video starting soon...',
              })}
            </CountdownText>
          </AiCountdownTooltip>
        )}
        </PlayerContainer>
      </PlayerWrapper>
    );
  },
);
